import { Component, OnInit, Input, Output, ViewChild, TemplateRef, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomAlertData, DefaultCustomAlertData } from 'src/app/data-types/alert-data';
import { Notes, NoteType } from 'src/app/models/notes';
import { AlertService } from 'src/app/services/alert.service';
import { NotesService } from 'src/app/services/notes.service';
import { Store } from 'src/app/store';
import { environment as env } from '../../../environments/environment';
import { INote } from 'src/app/data-types/note-data';

export interface INoteData {
    files: any[];
    content: string;
}

@UntilDestroy()
@Component({
    selector: 'app-note-form',
    templateUrl: './note-form.component.html',
    styleUrls: ['./note-form.component.scss'],
})
export class NoteFormComponent implements OnInit {
    @Input() noteData: INoteData = {
        files: [],
        content: '',
    };
    @Input() notePanelClass: string;
    @Input() recordId: string;
    @Input() type: string;
    @Output() noteAdded = new EventEmitter<INote>();

    @ViewChild('newLeadDlgTmpl', { read: TemplateRef })
    newLeadDlgTmpl: TemplateRef<any>;

    public showForm$ = new Subject<void>();

    uploadConfig;
    env = env;
    tinyInit = {
        height: 200,
        placeholder: 'Enter Note',
        menubar: false,
        plugins: ['preview anchor', 'searchreplace visualblocks link image imagetools'],
        toolbar:
            'undo redo | formatselect | bold italic | \
          alignleft aligncenter alignright alignjustify | \
          bullist numlist | removeformat |visualblocks',
    };

    canPostANote = true;

    constructor(public store: Store, public notesService: NotesService, private alertService: AlertService) {}

    ngOnInit(): void {
        this.showForm$.pipe(untilDestroyed(this)).subscribe(() => {
            this.showForm();
        });
        this.initUpload();
    }

    initUpload() {
        this.uploadConfig = {
            url: env.imageUploadPath + 'notes/upload',
            parallelUploads: 3,
            maxFiles: 10,
            maxFilesize: 5120,
            params: {},
            // The API's upload routes now require the admin session: send the cookie and
            // the app-name the API uses to pick auth.app.admin (Dropzone bypasses the interceptor).
            withCredentials: true,
            headers: {
                'api-key': '6bf9284a-ebf9-4afa-a185-4d2b9bb29e85',
                'app-name': 'admin',
            },
        };
    }

    showForm() {
        this.alertService.customAlert({
            ...DefaultCustomAlertData,
            template: this.newLeadDlgTmpl,
            cancelText: 'Cancel',
            confirmText: 'Add Note',
            confirmCb: this.addCallNote.bind(this),
            confirmClass: 'btn-styled',
            canConfirmCb: this.canPost.bind(this),
            styleClass: 'dlg-top-no-paddings',
            width: 640,
        } as CustomAlertData);
    }

    addCallNote() {
        this.notesService
            .addNote({
                type: this.type ?? NoteType.Prospect,
                recordId: this.recordId,
                content: this.noteData.content,
                files: this.noteData.files,
            } as Notes)
            .subscribe((note: INote) => {
                this.noteAdded.emit(note);
            });
    }

    onUploadError(e) {
        console.log('onUploadError', e);
        this.initUpload();
    }

    onTotalUploadProgress(event) {
        this.canPostANote = event[0] === 100;
    }

    onUploadSuccess(event) {
        this.noteData.files.push(event[1].data);
        this.initUpload();
    }

    canPost() {
        return this.canPostANote;
    }

    getFilePath(file) {
        return `${env.imageUploadPath}notes/file/${file}`;
    }

    removeFile(file: string) {
        this.noteData.files = this.noteData.files.filter((f) => f !== file);
    }
}
